// Legacy SelectColor (components/select/SelectColor.vue), the one colour picker every back-office port shares: Location Info's
// Location Color and Profile Color, Prep Times' Icon Color, and the catalog's Label Color and Category Color (B1261: two ports
// had drifted apart).
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { DropdownMenu } from 'radix-ui';
import { backofficeColor } from '@radish/ui-tokens';

import { SELECT_COLOR_OPTIONS } from './select-color-options.ts';

const CELL = 'rb-focus-host relative inline-flex size-[35px] items-center justify-center rounded-q outline-none';
const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab']);
const SELECTION_KEYS = new Set(['Enter', ' ']);

/**
 * Material's `circle` glyph: a 20 px disc in its 24 px q-icon; the chosen colour (`.ui-select-color-button`) adds legacy's 1.5 px
 * --q-border ring round the box (B806, B1062).
 */
function Swatch({ color, ringed }: { readonly color: string; readonly ringed: boolean }) {
  return (
    <span data-testid="color-swatch" className={`inline-flex size-6 shrink-0 box-content items-center justify-center rounded-full ${ringed ? 'border-[1.5px] border-line' : ''}`}>
      <span className="size-5 rounded-full" style={{ background: color }} />
    </span>
  );
}

/**
 * Legacy SelectColor: a 35 px round flat q-btn (app.scss's q-btn--round 8 px radius; B1155, B1228) whose `circle` icon is the
 * colour, ringed. Its q-menu (`max-width="230px"`, `auto-close`) opens under the trigger's left edge with no offset, holding a
 * q-card padded 8 with the options as 35 px cells, no gaps, only the chosen one ringed; a short `custom-options` list makes the
 * menu only as wide as its cells. When the menu would run past the window's right edge Quasar lines its right edge up with the
 * trigger's (Prep Times' Icon Color and the item editor's Label Color at 390). The card is the visible surface: white, or
 * `--q-dark` #212224 with no shadow in dark (B1256).
 *
 * The trigger and cells carry q-btn's layered focus helper in their own colour (`rb-focus-helper`: 15 % hovered, 22 % focused;
 * B1161, B1259). QMenu focuses the menu itself on open, so a keyboard open tints no cell until a navigation key moves into it,
 * and Enter or Space before that picks nothing. The cells are a radio group, so the chosen one reads as checked.
 *
 * Like QMenu (QMenu.js:121-128), a click elsewhere closes the menu and still reaches what it lands on, except on a dialog's
 * backdrop, which only closes the menu (B1260); a touch tap outside only closes it (B1296).
 */
export function SelectColor<C extends string>({ value, onChange, disabled = false, label, options = SELECT_COLOR_OPTIONS as unknown as readonly C[] }: {
  /** The chosen option, or a stored colour outside them (drawn on the trigger, no cell ringed). */
  readonly value: C | string;
  readonly onChange: (c: C) => void;
  readonly disabled?: boolean;
  /** The trigger's accessible name is `${label}: ${value}`. */
  readonly label: string;
  /** Legacy's `custom-options`; legacy's default list when left out. */
  readonly options?: readonly C[];
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  // Radix highlights an item under the pointer too, so `:focus-visible` can't tell a keyboard highlight (q-btn focused, 22 %)
  // from a hovered one (15 %): the menu marks which input moved the highlight last.
  const [keyboard, setKeyboard] = useState(false);
  // A keyboard open lands Radix's focus on the first cell; QMenu focuses the menu, so that cell shows no tint until a key moves.
  const [entry, setEntry] = useState(false);
  const [align, setAlign] = useState<'start' | 'end'>('start');
  // Which input opened the menu: only a keyboard open gets Radix's entry focus on the first cell.
  const openedBy = useRef<'pointer' | 'keyboard'>('pointer');
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next || triggerRef.current === null) return;
    setKeyboard(false);
    setEntry(openedBy.current === 'keyboard');
    // The menu's width: its 35 px cells and 8 px padding, at most 230 (q-menu max-width).
    const width = Math.min(230, options.length * 35 + 16);
    setAlign(triggerRef.current.getBoundingClientRect().left + width > window.innerWidth ? 'end' : 'start');
  };
  // A press on a dialog's backdrop (outside every dialog and menu while a dialog is open) only closes the menu: it is stopped
  // before the dialog's own outside-press listener (on the document) can see it. A touch tap anywhere outside the menu and its
  // trigger only closes it too, as QMenu always stops an outside touchstart (QMenu.js:121-124; B1296): the press is stopped
  // and the click the tap would make is swallowed, so a tablet's first tap acts on nothing else.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target === null || target.closest('[role="menu"]') !== null || (triggerRef.current?.contains(target) ?? false)) return;
      const backdrop = document.querySelector('[role="dialog"]') !== null && target.closest('[role="dialog"]') === null;
      const touch = e.pointerType === 'touch';
      if (!backdrop && !touch) return;
      e.stopPropagation();
      e.preventDefault();
      setOpen(false);
      if (!touch) return;
      const swallow = (click: MouseEvent) => { click.stopPropagation(); click.preventDefault(); };
      window.addEventListener('click', swallow, { capture: true, once: true });
      // A tap that never clicks (a scroll) must not swallow a later click.
      window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 700);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);
  // In the capture phase, before the focused cell's own handlers (Radix skips a handled event).
  const onKeyDownCapture = (e: KeyboardEvent) => {
    setKeyboard(true);
    if (!entry) return;
    // Enter or Space on QMenu's focused menu picks nothing.
    if (SELECTION_KEYS.has(e.key)) { e.preventDefault(); return; }
    if (!NAVIGATION_KEYS.has(e.key)) return;
    // The first navigation key into a keyboard-opened menu reveals the focused first cell instead of moving past it.
    setEntry(false);
    e.preventDefault();
  };
  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DropdownMenu.Trigger
        ref={triggerRef} disabled={disabled} onPointerDown={() => { openedBy.current = 'pointer'; }} onKeyDown={() => { openedBy.current = 'keyboard'; }} aria-label={`${label}: ${value}`} className={CELL} style={{ color: backofficeColor(value) }}>
        <span aria-hidden className="rb-focus-helper" />
        <Swatch color={backofficeColor(value)} ringed />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align} sideOffset={0} data-testid="color-menu" data-menu-align={align} data-keyboard={keyboard ? '' : undefined} data-entry={entry ? '' : undefined}
          onKeyDownCapture={onKeyDownCapture} onPointerMove={() => { setKeyboard(false); setEntry(false); }}
          className="z-[3000] flex w-max max-w-[230px] flex-wrap rounded-[4px] bg-dialog p-2 shadow-[0_1px_5px_rgba(0,0,0,0.2),0_2px_2px_rgba(0,0,0,0.14),0_3px_1px_-2px_rgba(0,0,0,0.12)] dark:shadow-none"
        >
          <DropdownMenu.RadioGroup value={value} onValueChange={(c) => onChange(c as C)} className="contents">
            {options.map((c) => (
              <DropdownMenu.RadioItem key={c} value={c} aria-label={c} className={`${CELL} cursor-pointer`} style={{ color: backofficeColor(c) }}>
                <span aria-hidden className="rb-focus-helper" />
                <Swatch color={backofficeColor(c)} ringed={c === value} />
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
